import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './api';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext.js';
import { SessionTimeoutProvider } from './context/SessionTimeoutContext';
import { DesignSystemThemeProvider } from './design-system/theme/ThemeProvider';
import { WhiteLabelProvider } from './design-system/whitelabel';
import Login from './components/Login/Login';
import Sidebar from './components/layout/Sidebar';
import { TopBar } from './components/layout/TopBar';
import { AppLayout } from './components/layout/AppLayout';
import { AIChatProvider } from './components/AIChat/AIChatProvider';
import AIChat from './components/AIChat/AIChat';
import { AnomalyAlertOverlay } from './components/AnomalyAlerts';

import './App.css';
import './styles/portal-foundation.css';
import './styles/antd-premium.css';
import './styles/portal-legacy-bridge.css';

import { useActivityTracker } from './features/navigation/hooks/useActivityTracker';

// Bootstrap navigation registry with all feature module pages
import './features/navigation/services/featureRegistrations';

// All route definitions live in a dedicated module to keep this shell thin.
import AppRoutes, { PageLoader } from './routes/AppRoutes';

// Shell-only lazy components (not part of the route tree)
const CommandPalette = lazy(() => import('./features/navigation/components/CommandPalette'));
const AiChatBar = lazy(() => import('./features/navigation/components/AiChatBar'));

function AuthenticatedApp() {
    const { toggleTheme } = useTheme();

    // Track page visits for activity-based navigation
    useActivityTracker();

    // Listen for toggleTheme CustomEvent from Command Palette / AI Chat Bar
    React.useEffect(() => {
        const handler = () => toggleTheme();
        window.addEventListener('toggleTheme', handler);
        return () => window.removeEventListener('toggleTheme', handler);
    }, [toggleTheme]);

    return (
        <AIChatProvider>
            <div className="app-shell">
                <TopBar />
                <Sidebar />
                <Suspense fallback={null}><CommandPalette /></Suspense>
                <Suspense fallback={null}><AiChatBar /></Suspense>
                <AIChat />
                <AnomalyAlertOverlay />

                <main className="app-shell__main">
                    <Suspense fallback={<PageLoader />}>
                        <AppLayout>
                            <AppRoutes />
                        </AppLayout>
                    </Suspense>
                </main>
            </div>
        </AIChatProvider>
    );
}

function AppContent() {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="login-loading">
                <div className="login-loading__content">
                    <div className="login-loading__spinner" />
                    <span>Loading...</span>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Login />;
    }

    return (
        <SessionTimeoutProvider>
            <AuthenticatedApp />
        </SessionTimeoutProvider>
    );
}

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <Router>
                <AuthProvider>
                    <WhiteLabelProvider>
                        <ThemeProvider>
                            <DesignSystemThemeProvider>
                                <AppContent />
                            </DesignSystemThemeProvider>
                        </ThemeProvider>
                    </WhiteLabelProvider>
                </AuthProvider>
            </Router>
        </QueryClientProvider>
    );
}

export default App;
